import axiosInstance from 'api/axiosConfig'
import { ENDPOINTS } from 'api/endpoints'
import handleApiError from 'api/utils/handleApiError'
import { useEffect, useState } from 'react'
import { toast } from 'react-toastify'

import { i18n } from '../i18n'

const LockIcon = () => (
  <svg className="w-4 h-4 flex-shrink-0 text-gray-500" viewBox="0 0 24 24" fill="currentColor">
    <path d="M18 8h-1V6c0-2.76-2.24-5-5-5S7 3.24 7 6v2H6c-1.1 0-2 .9-2 2v10c0 1.1.9 2 2 2h12c1.1 0 2-.9 2-2V10c0-1.1-.9-2-2-2zm-6 9c-1.1 0-2-.9-2-2s.9-2 2-2 2 .9 2 2-.9 2-2 2z" />
  </svg>
)
const ShieldIcon = () => (
  <svg className="w-4 h-4 flex-shrink-0 text-gray-500" viewBox="0 0 24 24" fill="currentColor">
    <path d="M12 1L3 5v6c0 5.55 3.84 10.74 9 12 5.16-1.26 9-6.45 9-12V5l-9-4zm0 10.99h7c-.53 4.12-3.28 7.79-7 8.94V12H5V6.3l7-3.11v8.8z" />
  </svg>
)
const HeartIcon = () => (
  <svg className="w-4 h-4 flex-shrink-0 text-gray-500" viewBox="0 0 24 24" fill="currentColor">
    <path d="M12 21.35l-1.45-1.32C5.4 15.36 2 12.28 2 8.5 2 5.42 4.42 3 7.5 3c1.74 0 3.41.81 4.5 2.09C13.09 3.81 14.76 3 16.5 3 19.58 3 22 5.42 22 8.5c0 3.78-3.4 6.86-8.55 11.54L12 21.35z" />
  </svg>
)
const ArrowRightIcon = () => (
  <svg className="w-4 h-4 flex-shrink-0" viewBox="0 0 24 24" fill="currentColor">
    <path d="M12 4l-1.41 1.41L16.17 11H4v2h12.17l-5.58 5.59L12 20l8-8-8-8z" />
  </svg>
)

const Contact = () => {
  const title = i18n.translate('contact.title')
  const subtitle = i18n.translate('contact.subtitle')
  const labelName = i18n.translate('contact.name')
  const labelEmail = i18n.translate('contact.email')
  const labelSubject = i18n.translate('contact.subject')
  const labelMessage = i18n.translate('contact.message')
  const placeholderName = i18n.translate('contact.namePlaceholder')
  const placeholderEmail = i18n.translate('contact.emailPlaceholder')
  const placeholderSubject = i18n.translate('contact.subjectPlaceholder')
  const placeholderMessage = i18n.translate('contact.messagePlaceholder')
  const consentLabel = i18n.translate('contact.consentLabel')
  const submitLabel = i18n.translate('contact.submit')
  const secure = i18n.translate('contact.secure')
  const confidentiality = i18n.translate('contact.confidentiality')
  const quebec = i18n.translate('contact.quebec')

  const [isSubmitting, setIsSubmitting] = useState(false)
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: '',
    consent: false,
  })

  useEffect(() => {
    document.title = title
  }, [title])

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target
    const checked = type === 'checkbox' ? (e.target as HTMLInputElement).checked : undefined
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }))
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setIsSubmitting(true)
    try {
      await axiosInstance.post(ENDPOINTS.CONTACT.SUBMIT, {
        name: formData.name,
        email: formData.email,
        subject: formData.subject || undefined,
        message: formData.message,
        consent: formData.consent,
      })
      toast.success(i18n.translate('contact.success'))
      setFormData({ name: '', email: '', subject: '', message: '', consent: false })
    } catch (err) {
      handleApiError(i18n.translate('contact.error'), err)
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <div className="min-h-screen bg-[#F7F8FA]">
      <main className="max-w-2xl mx-auto px-4 py-10 md:py-14">
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-8 md:p-10">
          <h1 className="text-2xl md:text-3xl font-bold text-gray-900 mb-2">
            {title}
          </h1>
          <p className="text-gray-500 text-sm md:text-base mb-8">
            {subtitle}
          </p>

          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <label htmlFor="contact-name" className="block text-xs font-bold uppercase tracking-wide text-gray-800 mb-2">
                {labelName}
              </label>
              <input
                id="contact-name"
                name="name"
                type="text"
                value={formData.name}
                onChange={handleChange}
                placeholder={placeholderName}
                className="w-full px-4 py-3 rounded-lg border border-gray-200 bg-white text-gray-900 placeholder-gray-400 text-sm focus:outline-none focus:ring-2 focus:ring-imovPrimary focus:border-transparent"
              />
            </div>

            <div>
              <label htmlFor="contact-email" className="block text-xs font-bold uppercase tracking-wide text-gray-800 mb-2">
                {labelEmail}
              </label>
              <input
                id="contact-email"
                name="email"
                type="email"
                value={formData.email}
                onChange={handleChange}
                placeholder={placeholderEmail}
                className="w-full px-4 py-3 rounded-lg border border-gray-200 bg-white text-gray-900 placeholder-gray-400 text-sm focus:outline-none focus:ring-2 focus:ring-imovPrimary focus:border-transparent"
              />
            </div>

            <div>
              <label htmlFor="contact-subject" className="block text-xs font-bold uppercase tracking-wide text-gray-800 mb-2">
                {labelSubject}
              </label>
              <select
                id="contact-subject"
                name="subject"
                value={formData.subject}
                onChange={handleChange}
                className="w-full px-4 py-3 rounded-lg border border-gray-200 bg-white text-gray-700 text-sm focus:outline-none focus:ring-2 focus:ring-imovPrimary focus:border-transparent appearance-none bg-[url('data:image/svg+xml;charset=utf-8,%3Csvg%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%20width%3D%2224%22%20height%3D%2224%22%20viewBox%3D%220%200%2024%2024%22%20fill%3D%22none%22%20stroke%3D%22%236b7280%22%20stroke-width%3D%222%22%3E%3Cpath%20d%3D%22M6%209l6%206%206-6%22%2F%3E%3C%2Fsvg%3E')] bg-[length:1.25rem] bg-[right_0.5rem_center] bg-no-repeat pr-10"
              >
                <option value="">{placeholderSubject}</option>
                <option value="general">{i18n.translate('contact.subjectGeneral')}</option>
                <option value="support">{i18n.translate('contact.subjectSupport')}</option>
                <option value="partnership">{i18n.translate('contact.subjectPartnership')}</option>
              </select>
            </div>

            <div>
              <label htmlFor="contact-message" className="block text-xs font-bold uppercase tracking-wide text-gray-800 mb-2">
                {labelMessage}
              </label>
              <textarea
                id="contact-message"
                name="message"
                rows={4}
                value={formData.message}
                onChange={handleChange}
                placeholder={placeholderMessage}
                className="w-full px-4 py-3 rounded-lg border border-gray-200 bg-white text-gray-900 placeholder-gray-400 text-sm focus:outline-none focus:ring-2 focus:ring-imovPrimary focus:border-transparent resize-y min-h-[100px]"
              />
            </div>

            <div className="flex items-start gap-3">
              <input
                id="contact-consent"
                name="consent"
                type="checkbox"
                checked={formData.consent}
                onChange={handleChange}
                className="mt-1 w-4 h-4 rounded-full border-gray-300 text-imovPrimary focus:ring-imovPrimary"
              />
              <label htmlFor="contact-consent" className="text-sm text-gray-600 leading-relaxed">
                {consentLabel}
              </label>
            </div>

            <button
              type="submit"
              disabled={isSubmitting}
              className="w-full flex items-center justify-center gap-2 py-3.5 px-4 rounded-lg bg-imovPrimary hover:bg-imovPrimaryDark text-white font-semibold text-sm transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
            >
              {isSubmitting ? i18n.translate('contact.sending') : submitLabel}
              {!isSubmitting && <ArrowRightIcon />}
            </button>
          </form>

          <div className="flex flex-wrap justify-center gap-6 md:gap-10 mt-8 pt-6 border-t border-gray-100">
            <div className="flex items-center gap-2 text-gray-500 text-xs font-medium uppercase tracking-wide">
              <LockIcon />
              <span>{secure}</span>
            </div>
            <div className="flex items-center gap-2 text-gray-500 text-xs font-medium uppercase tracking-wide">
              <ShieldIcon />
              <span>{confidentiality}</span>
            </div>
            <div className="flex items-center gap-2 text-gray-500 text-xs font-medium uppercase tracking-wide">
              <HeartIcon />
              <span>{quebec}</span>
            </div>
          </div>
        </div>
      </main>
    </div>
  )
}

export default Contact
